import axios from 'axios';

const API = `${window.__BACKEND_URL__}/api`;

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export const reportLayoutOf = (company) => {
  const b = company?.document_branding || {};
  return {
    logoPosition: b.report_logo_position || b.logo_position || 'left',
    orientation: b.report_orientation === 'landscape' ? 'landscape' : 'portrait',
    showCharts: b.report_show_charts !== false,
    footerText: b.report_footer_text || '',
    showGeneratedBy: b.report_show_generated_by !== false,
    primary: b.primary_color || '#1E88E5',
    ink: b.text_color || '#0F172A',
    muted: b.muted_color || '#64748B',
    line: b.border_color || '#E2E8F0',
    subtle: b.subtle_bg || '#F8FAFC',
  };
};

// Serialise the first <svg> inside `el` to a PNG data URL (for the server PDF).
export async function captureChartPng(el) {
  const svg = el?.querySelector?.('svg');
  if (!svg) return null;
  try {
    const clone = svg.cloneNode(true);
    const { width, height } = svg.getBoundingClientRect();
    clone.setAttribute('width', width); clone.setAttribute('height', height);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    bg.setAttribute('width', '100%'); bg.setAttribute('height', '100%'); bg.setAttribute('fill', '#ffffff');
    clone.insertBefore(bg, clone.firstChild);
    clone.querySelectorAll('text').forEach((t) => { t.setAttribute('fill', '#334155'); });
    const blob = new Blob([new XMLSerializer().serializeToString(clone)], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
    const canvas = document.createElement('canvas');
    canvas.width = width * 2; canvas.height = height * 2;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

// Branded browser print (→ Save as PDF): company logo/name, title, period, summary, optional chart, table, footer.
export function printBranded({ containerRef, chartEl, title, period, company, user, summary = [] }) {
  const lay = reportLayoutOf(company);
  const w = window.open('', '_blank');
  if (!w) return window.print();
  const logoUrl = company?.company_logo ? `${API}/company/logo/${company.company_logo}` : null;
  const css = `
    *{box-sizing:border-box} body{font-family:Inter,Arial,sans-serif;background:#fff;color:${lay.ink};padding:28px 32px;margin:0}
    .hdr{display:flex;align-items:flex-start;justify-content:space-between;gap:24px;padding-bottom:14px;border-bottom:2px solid ${lay.primary};margin-bottom:18px;
      ${lay.logoPosition === 'center' ? 'flex-direction:column;align-items:center;text-align:center' : ''}${lay.logoPosition === 'right' ? 'flex-direction:row-reverse' : ''}}
    .brand img{max-height:56px;max-width:220px;object-fit:contain} .brand .name{font-size:16px;font-weight:700;margin-top:4px}
    .meta{text-align:${lay.logoPosition === 'center' ? 'center' : lay.logoPosition === 'right' ? 'left' : 'right'}}
    .meta h1{margin:0;font-size:22px;color:${lay.primary}} .meta p{margin:3px 0 0;font-size:11px;color:${lay.muted}}
    .sum{display:flex;gap:10px;margin-bottom:16px} .sum div{flex:1;background:${lay.subtle};border:1px solid ${lay.line};border-radius:6px;padding:8px 12px}
    .sum small{display:block;font-size:9px;letter-spacing:.06em;text-transform:uppercase;color:${lay.muted}} .sum b{font-size:15px;color:${lay.primary}}
    .chart{margin-bottom:16px;text-align:center} .chart svg{max-width:100%;height:auto;background:#fff;border:1px solid ${lay.line};border-radius:6px}
    .chart svg text{fill:#334155 !important} .chart svg line,.chart svg path.recharts-cartesian-grid-horizontal{stroke:${lay.line}}
    table{border-collapse:collapse;width:100%;font-size:10.5px} th,td{border-bottom:1px solid ${lay.line};padding:5px 8px;text-align:left;vertical-align:top}
    th{background:${lay.primary};color:#fff;font-size:10px;text-transform:uppercase;letter-spacing:.04em} tr:nth-child(even) td{background:${lay.subtle}}
    th button{all:unset;color:inherit} th svg{display:none} td button{all:unset;color:inherit} td svg{display:none}
    .no-print, [data-no-print]{display:none !important}
    .ftr{position:fixed;bottom:10px;left:32px;right:32px;display:flex;justify-content:space-between;font-size:9px;color:${lay.muted};border-top:1px solid ${lay.line};padding-top:5px}
    @page{size:${lay.orientation};margin:14mm 12mm 18mm}
  `;
  const d = w.document;
  d.title = title;
  const style = d.createElement('style'); style.textContent = css; d.head.appendChild(style);
  const hdr = d.createElement('div'); hdr.className = 'hdr';
  const brand = d.createElement('div'); brand.className = 'brand';
  if (logoUrl) { const img = d.createElement('img'); img.src = logoUrl; img.alt = ''; brand.appendChild(img); }
  const nm = d.createElement('div'); nm.className = 'name'; nm.textContent = company?.company_name || ''; brand.appendChild(nm);
  const meta = d.createElement('div'); meta.className = 'meta';
  meta.innerHTML = `<h1>${esc(title)}</h1>${period ? `<p>Period: ${esc(period)}</p>` : ''}<p>Generated ${esc(new Date().toLocaleString())}${lay.showGeneratedBy && user?.name ? ` · Prepared by ${esc(user.name)}` : ''}</p>`;
  hdr.appendChild(brand); hdr.appendChild(meta); d.body.appendChild(hdr);
  if (summary.length) {
    const s = d.createElement('div'); s.className = 'sum';
    s.innerHTML = summary.slice(0, 6).map((x) => `<div><small>${esc(x.label)}</small><b>${esc(x.value)}</b></div>`).join('');
    d.body.appendChild(s);
  }
  if (lay.showCharts && chartEl?.querySelector?.('svg')) {
    const c = d.createElement('div'); c.className = 'chart';
    const svg = chartEl.querySelector('svg').cloneNode(true);
    const { width, height } = chartEl.querySelector('svg').getBoundingClientRect();
    svg.setAttribute('width', width); svg.setAttribute('height', height);
    c.appendChild(svg); d.body.appendChild(c);
  }
  if (containerRef?.current) {
    const container = d.createElement('div');
    container.innerHTML = containerRef.current.innerHTML;
    container.querySelectorAll('[data-testid="chart-switcher"], [data-testid="chart-canvas"]').forEach((n) => n.remove());
    d.body.appendChild(container);
  }
  const ftr = d.createElement('div'); ftr.className = 'ftr';
  ftr.innerHTML = `<span>${esc(lay.footerText || company?.company_name || '')}</span><span>${esc(title)}</span>`;
  d.body.appendChild(ftr);
  w.focus();
  // Leave the preview open so the admin can review it; the print dialog offers "Save as PDF".
  setTimeout(() => { try { w.print(); } catch { /* popup closed */ } }, 500);
}

// Build the spec POSTed to /reports/v2/pdf and /reports/v2/email-pdf.
export async function buildPdfSpec({ title, period, columns, rows, summary = [], chartEl, subtitle }) {
  const chart_png = chartEl ? await captureChartPng(chartEl) : null;
  const cols = columns.filter((c) => c.key && !c.noExport);
  const flat = rows.map((r) => Object.fromEntries(cols.map((c) => {
    const raw = r[c.key];
    const rendered = c.exportValue ? c.exportValue(r) : (typeof raw === 'object' && raw !== null ? JSON.stringify(raw) : raw);
    return [c.key, rendered == null ? '' : String(rendered)];
  })));
  return { title, period, subtitle, columns: cols.map((c) => ({ key: c.key, label: c.label })), rows: flat, summary, chart_png };
}

// Server-generated branded PDF download (identical layout every time).
export async function downloadServerPdf({ token, ...spec }) {
  const body = await buildPdfSpec(spec);
  const res = await axios.post(`${API}/reports/v2/pdf`, body, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url; a.download = `${spec.title.replace(/[^\w\- ]+/g, '').trim().replace(/\s+/g, '_') || 'report'}.pdf`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
